import { queryOptions } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export type Capability =
  | 'admin'
  | 'audit.read'
  | 'projects.read_all'
  | 'projects.manage_all'
  | 'queue.reorder'
  | 'queue.work'
  | 'prices.read'
  | 'prices.manage'
  | 'products.manage'
  | 'dashboard.view'
  | 'reminders.send'
  | 'relux.create'
  | 'relux.assign_follow_up'
  | 'sync.view'

export type Me = {
  id: string
  full_name: string
  display_name: string
  email: string
  phone: string | null
  role: string
  role_label: string
  region: { id: string; code: string; name: string } | null
  capabilities: Capability[]
  flags: Record<string, boolean>
  notification_prefs: Record<string, unknown>
  impersonating: boolean
  real_user_id: string
}

export const meQueryKey = ['me'] as const

/** The signed-in (or viewed-as) user. Persisted, so the app can boot with no signal. */
export const meQuery = queryOptions({
  queryKey: meQueryKey,
  queryFn: async (): Promise<Me | null> => {
    const { data, error } = await supabase.rpc('me')
    if (error) throw error
    return (data as Me | null) ?? null
  },
  staleTime: 5 * 60_000,
  gcTime: 7 * 24 * 60 * 60_000,
})

export function can(me: Me | null | undefined, cap: Capability) {
  if (!me) return false
  return me.capabilities.includes('admin') || me.capabilities.includes(cap)
}

export function flag(me: Me | null | undefined, key: string) {
  return Boolean(me?.flags[key])
}

/** Reps and internal sales get the big-button field interface; everyone else the staff one. */
export function isFieldUser(me: Me | null | undefined) {
  return !!me && !can(me, 'projects.read_all')
}
